import type { CutDirection } from "./beatmap";

export type TutorialKamae =
  | "chudan"
  | "jodan"
  | "gedan"
  | "hasso"
  | "waki"
  | "flowing";

export interface TutorialGhostFrame {
  t: number;
  tip: [number, number, number];
  grip: [number, number, number];
  label?: string;
  kamae?: TutorialKamae;
}

export interface GhostDemo {
  title: string;
  description: string;
  loopSeconds: number;
  frames: TutorialGhostFrame[];
}

export interface TutorialPracticeCheck {
  label: string;
  kind?: "cut" | "dodge-left" | "dodge-right" | "duck";
  dir: CutDirection;
  laneX: number;
  height: number;
  requireTenouchi?: boolean;
  prompt?: string;
  success?: string;
  correction?: string;
  sequenceGroup?: string;
}

// Mirrors src/ui/TeachPanel.tsx's ACTOR_ASSETS keys exactly — kept as a
// plain string union here (not imported from ui/) so game/ doesn't take a
// dependency on ui/; the world-space panel looks the id up in its own
// ACTOR_ASSETS map by the same key name.
export type VrIllustrationId =
  | "theDoorOpens"
  | "noSparring"
  | "oneKatana"
  | "likeItMatters"
  | "chudanHonestCut"
  | "honestCutTriptych"
  | "theFirstCut"
  | "doNotClash"
  | "howTheDojoJudges"
  | "theBladeRemembers"
  | "threeWords"
  | "firstRepetition";

export interface OnboardingScreen {
  kicker: string;
  title: string;
  seiji: string[];
  instruction: string;
  ghostCaption: string;
  failureHint?: string;
  secondaryFailureHint?: string;
  successFeedback?: string;
  primaryButton: string;
  secondaryButton?: string;
  initialPracticeText?: string;
  vrImage?: VrIllustrationId;
}

export interface Tutorial {
  phase: number;
  title: string;
  element: string;
  onboardingScreens?: OnboardingScreen[];
  kamaeTaught?: string;
  elicitByPlacement?: string;
  ghostDemo: GhostDemo;
  seijiLines: string[];
  training: {
    reps: number;
    feedback: "yes-no";
    replayable: "infinite";
    tolerance: "generous";
    drill: string;
    check?: TutorialPracticeCheck;
    checks?: TutorialPracticeCheck[];
  };
  beginnerError: string;
  correction: string;
  skippable: boolean;
  unlocks: string;
  arcBeat?: string;
}

const ONBOARDING_SCREENS: OnboardingScreen[] = [
  {
    kicker: "FIRST ENTRY",
    title: "The Door Opens",
    seiji: [
      "You came here angry. Good - anger is honest. But it is not enough.",
      "A man who only knows anger is easy to read. The man who beat you read every part of you.",
    ],
    instruction: "Take your place in the center of the dojo. Face Seiji.",
    ghostCaption:
      "No blade yet. Only the dojo, the master, and the wound that brought Masaru here.",
    primaryButton: "Continue",
    vrImage: "theDoorOpens",
  },
  {
    kicker: "FIRST ENTRY",
    title: "No Sparring",
    seiji: [
      "You want a fight. That is why you lost.",
      "Tonight you will not fight me. You will learn how to stand, how to hold, and how not to lie with a sword.",
    ],
    instruction:
      "Listen first. The lesson starts with how you stand and hold, not how you strike.",
    ghostCaption: "",
    primaryButton: "Continue",
    vrImage: "noSparring",
  },
  {
    kicker: "GRIP",
    title: "One Katana",
    seiji: [
      "A sword is not a fist, and not a fencer's needle. Both hands.",
      "The left holds the truth; the right points it.",
    ],
    instruction:
      "Use one controller as one katana. Put both hands on the same controller.",
    ghostCaption:
      "Left hand at the end of the handle. Right hand near the guard. One blade.",
    failureHint: "Both hands, Masaru. You are not poking. You are cutting.",
    successFeedback: "Good. Now the sword has a body behind it.",
    primaryButton: "Practice Grip",
    vrImage: "oneKatana",
  },
  {
    kicker: "GRIP",
    title: "Like It Matters",
    seiji: [
      "Hold it like it matters. It does.",
      "A loose sword lies. A clenched sword panics. Between those two, there is control.",
    ],
    instruction:
      "Keep the lower fingers firm. Do not squeeze the controller hard yet.",
    ghostCaption:
      "Firm last fingers. Relaxed shoulders. The blade stays alive without shaking.",
    failureHint:
      "The blade is shaking. Do not strangle the handle - let it breathe.",
    successFeedback: "Better. Control is not stiffness.",
    primaryButton: "Continue",
    vrImage: "likeItMatters",
  },
  {
    kicker: "GUARD",
    title: "Chudan",
    seiji: [
      "Before a cut, there is guard. Chudan: point the blade to the center and let the body settle.",
      "A restless blade tells the enemy where fear lives.",
    ],
    instruction:
      "Hold the controller with both hands in front of your chest. Point the blade toward the center.",
    ghostCaption:
      "Middle guard: quiet hands, forward point, ready body.",
    failureHint: "Let the blade settle. Bring the point back to the center.",
    successFeedback: "There. Do not rush to swing. Begin from guard.",
    primaryButton: "Continue",
    vrImage: "chudanHonestCut",
  },
  {
    kicker: "CUT",
    title: "An Honest Cut",
    seiji: [
      "A cut is three things at once: the right line, an honest commitment, and a finish.",
      "Miss any one, and you have only waved a stick.",
    ],
    instruction:
      "The cut starts in guard, passes through the target, and finishes beyond it.",
    ghostCaption: "Line. Commitment. Finish. One motion, not three rules.",
    failureHint:
      "Do not stop at contact. The cut must pass through the target.",
    primaryButton: "Watch the Cut",
    vrImage: "honestCutTriptych",
  },
  {
    kicker: "CUT",
    title: "The First Cut",
    seiji: ["Now answer once. No anger. No flourish. Just the line."],
    instruction:
      "Cut the amber target from top to bottom. Continue past the target.",
    ghostCaption:
      "The amber target opens on the center line. The cut passes through and finishes low.",
    failureHint: "Again. From the body, not the wrist.",
    secondaryFailureHint: "You stopped at contact. Finish the arc.",
    successFeedback: "There. Not strong. True.",
    primaryButton: "Practice Cut",
    vrImage: "theFirstCut",
  },
  {
    kicker: "DANGER",
    title: "Do Not Clash",
    seiji: [
      "Not everything is meant to be struck. A good blade does not clash head-on - it splits, or it slips aside.",
      "When you see danger, you move. You do not swing.",
    ],
    instruction: "When red danger passes, lean or duck. Keep the blade quiet.",
    ghostCaption:
      "The red barrier passes. The body slips aside; the sword does not answer.",
    failureHint: "You tried to cut danger. The edge is not a shield. Move.",
    successFeedback: "Good. The body saved the blade.",
    primaryButton: "Practice Moving",
    vrImage: "doNotClash",
  },
  {
    kicker: "JUDGMENT",
    title: "How the Dojo Judges",
    seiji: [
      "You will be judged. 一本 - a perfect cut. Lesser marks belong to lesser cuts.",
      "Miss, and it is nothing.",
      "The mark is not an insult. It tells you what the cut lacked.",
    ],
    instruction:
      "MISS 外れ - wrong angle, or the target passed unanswered. LATE 可 - the cut lands, but well behind the beat. GOOD 良い - on time, but loose - off-line, off-center, or without real force behind it. GREAT 素晴 - solid across the board: on time, true to the line, centered, real power in the wrist. EXCELLENT 技あり - all of that, tightened - closer to dead-on in every measure. IPPON 一本 - everything at its peak at once: timing, line, center, and power, landing true together.",
    ghostCaption:
      "A clean cut flashes gold. A weak cut fades. A miss leaves silence.",
    primaryButton: "Continue",
    vrImage: "howTheDojoJudges",
  },
  {
    kicker: "COMBO",
    title: "The Blade Remembers",
    seiji: [
      "String true cuts together and the blade remembers - it wakes, and burns gold.",
      "That is mastery answering you.",
    ],
    instruction:
      "String three clean cuts together and the blade wakes - steel rising to gold.",
    ghostCaption: "Three honest cuts. The blade glow rises from steel to gold.",
    primaryButton: "Continue",
    vrImage: "theBladeRemembers",
  },
  {
    kicker: "WORDS",
    title: "Three Words",
    seiji: [
      "Guard. Grip. Awareness. Kamae, tenouchi, zanshin.",
      "You will feel them before you can name them.",
    ],
    instruction:
      "Kamae - ready before. Tenouchi - the grip at impact. Zanshin - awareness after.",
    ghostCaption: "Before. Impact. After. Three moments of one cut.",
    primaryButton: "Continue",
    vrImage: "threeWords",
  },
  {
    kicker: "PRACTICE",
    title: "First Repetition",
    seiji: [
      "Now show me the beginning. Not victory. Beginning.",
      "One blade. Both hands. Cut what opens. Move from what threatens.",
    ],
    instruction:
      "1. Hold one controller with both hands. 2. Cut the amber target. 3. Move aside from red danger.",
    ghostCaption:
      "The full lesson, once more, slowly: grip, guard, cut, move.",
    initialPracticeText:
      "Practice at your own pace. The dojo will answer, but it will not stop you.",
    failureHint: "Again. The first lesson does not need speed.",
    successFeedback: "Enough. The door can open.",
    primaryButton: "Enter the Dojo",
    secondaryButton: "Practice Again",
    vrImage: "firstRepetition",
  },
];

export const ONBOARDING_TUTORIAL: Tutorial = {
  phase: 0,
  title: "The Door Opens",
  element:
    "Masaru meets Seiji, learns one blade with both hands, and repeats the first lesson.",
  onboardingScreens: ONBOARDING_SCREENS,
  kamaeTaught: "chudan",
  elicitByPlacement: "neutral targets establish the center line",
  ghostDemo: {
    title: "Grip, guard, cut, move",
    description:
      "The full lesson, once more, slowly: grip, guard, cut, move.",
    loopSeconds: 5.2,
    frames: [
      {
        t: 0,
        tip: [0, 1.58, -0.74],
        grip: [0, 1.22, -0.58],
        label: "chudan",
        kamae: "chudan",
      },
      {
        t: 1.05,
        tip: [0, 1.58, -0.74],
        grip: [0, 1.22, -0.58],
        label: "grip",
      },
      {
        t: 2.05,
        tip: [0, 2.0, -0.74],
        grip: [0, 1.68, -0.58],
        label: "guard",
      },
      {
        t: 3.0,
        tip: [0, 0.94, -0.74],
        grip: [0, 1.1, -0.58],
        label: "finish",
      },
      {
        t: 4.0,
        tip: [-0.42, 1.42, -0.74],
        grip: [-0.38, 1.22, -0.58],
        label: "move",
      },
      { t: 5.2, tip: [0, 1.58, -0.74], grip: [0, 1.22, -0.58] },
    ],
  },
  seijiLines: ONBOARDING_SCREENS.map((screen) => screen.seiji.join("\n\n")),
  training: {
    reps: 2,
    feedback: "yes-no",
    replayable: "infinite",
    tolerance: "generous",
    drill:
      "1. Hold one controller with both hands. 2. Cut the amber target. 3. Move aside from red danger.",
    check: {
      label: "amber target",
      kind: "cut",
      dir: "down",
      laneX: 0,
      height: 1.42,
      prompt: "Cut the amber target from top to bottom. Finish the arc.",
    },
    checks: [
      {
        label: "amber target",
        kind: "cut",
        dir: "down",
        laneX: 0,
        height: 1.42,
        prompt: "Cut the amber target from top to bottom. Finish the arc.",
        success: "There. Not strong. True.",
        correction: "Again. From the body, not the wrist.",
      },
      {
        label: "red danger",
        kind: "dodge-left",
        dir: "any",
        laneX: 0.5,
        height: 1.28,
        prompt: "Danger is not a target. Move left; do not swing.",
        success: "Good. The body saved the blade.",
        correction: "You tried to cut danger. The edge is not a shield. Move.",
      },
    ],
  },
  beginnerError:
    "wrist flicks, stopping at contact, or trying to cut red danger",
  correction: "Again. The first lesson does not need speed.",
  skippable: true,
  unlocks: "phase I teaching",
  arcBeat:
    "Masaru enters defeated and angry; Seiji sees the anger but refuses to worship it.",
};

export const PHASE_TUTORIALS: Tutorial[] = [
  {
    phase: 1,
    title: "Kamae",
    element: "vertical cut (shomen) and high guard",
    kamaeTaught: "jodan",
    elicitByPlacement: "high targets -> jodan top-down shomen",
    ghostDemo: {
      title: "Jodan to shomen",
      description: "Raise overhead, then cut straight down and settle.",
      loopSeconds: 3.8,
      frames: [
        {
          t: 0,
          tip: [0, 2.14, -0.76],
          grip: [0, 1.78, -0.58],
          label: "jodan",
          kamae: "jodan",
        },
        { t: 1.0, tip: [0, 2.14, -0.76], grip: [0, 1.78, -0.58] },
        {
          t: 1.85,
          tip: [0, 1.34, -0.76],
          grip: [0, 1.36, -0.58],
          label: "shomen",
        },
        {
          t: 2.65,
          tip: [0, 0.96, -0.76],
          grip: [0, 1.1, -0.58],
          label: "finish",
          kamae: "gedan",
        },
        { t: 3.8, tip: [0, 2.14, -0.76], grip: [0, 1.78, -0.58] },
      ],
    },
    seijiLines: [
      "The first cut falls straight - not fast, true.",
      "Raise the blade overhead - jodan, the high guard - and let it fall with your whole body.",
      "Keep both hands on the same sword. The controller is the handle; your body is the engine.",
      "A hundred wild cuts lose to one honest one. Do less.",
    ],
    training: {
      reps: 3,
      feedback: "yes-no",
      replayable: "infinite",
      tolerance: "generous",
      drill: "Cut the high target straight down from overhead.",
      check: {
        label: "shomen",
        dir: "down",
        laneX: 0,
        height: 1.72,
        prompt: "Raise to jodan, then cut straight down.",
        success: "Straight line. The sword fell as one piece.",
        correction:
          "Overhead, then straight down. Do not let the wrist steal the cut.",
      },
    },
    beginnerError:
      "wrist-flick, one hand driving the controller, or cutting across instead of down",
    correction:
      "From the body, not the wrist. Overhead, then straight down. Again.",
    skippable: true,
    unlocks: "phase I challenge high shomen targets",
    arcBeat: "Masaru wants to spar; Seiji gives him one cut, repeated.",
  },
  {
    phase: 2,
    title: "Kihon",
    element: "horizontal cut (yoko) and tenouchi",
    ghostDemo: {
      title: "Yoko with tenouchi",
      description: "Cut across; squeeze only at impact, then release.",
      loopSeconds: 3.4,
      frames: [
        {
          t: 0,
          tip: [-0.62, 1.42, -0.76],
          grip: [-0.14, 1.38, -0.58],
          label: "prepare",
          kamae: "chudan",
        },
        {
          t: 1.1,
          tip: [0.62, 1.42, -0.76],
          grip: [0.14, 1.38, -0.58],
          label: "squeeze",
        },
        { t: 2.1, tip: [0.62, 1.42, -0.76], grip: [0.14, 1.38, -0.58] },
        { t: 3.4, tip: [-0.62, 1.42, -0.76], grip: [-0.14, 1.38, -0.58] },
      ],
    },
    seijiLines: [
      "Power is not in the arm. It is here - at the instant of contact. Squeeze. Then release.",
      "You try to force it. Don't. Time it.",
    ],
    training: {
      reps: 4,
      feedback: "yes-no",
      replayable: "infinite",
      tolerance: "generous",
      drill: "Cut horizontally with a crisp speed spike at contact.",
      check: {
        label: "yoko",
        dir: "right",
        laneX: 0,
        height: 1.42,
        requireTenouchi: true,
        prompt: "Cut across and squeeze only at the contact.",
        success: "Clean yoko. The squeeze arrived at the cut, not before it.",
        correction: "Do not push the blade. Let it bite, then release.",
      },
    },
    beginnerError: "constant-speed wave with no squeeze",
    correction: "You are pushing the blade. Let it bite, then let go.",
    skippable: true,
    unlocks: "phase II horizontal targets and tenouchi reward",
    arcBeat: "First hint that force is not the answer.",
  },
  {
    phase: 3,
    title: "Suburi",
    element: "follow-through and zanshin",
    ghostDemo: {
      title: "Finish the arc",
      description: "The blade continues past contact and holds awareness.",
      loopSeconds: 3.6,
      frames: [
        {
          t: 0,
          tip: [0, 2.02, -0.76],
          grip: [0, 1.72, -0.58],
          label: "begin",
          kamae: "jodan",
        },
        { t: 1.0, tip: [0, 1.28, -0.76], grip: [0, 1.28, -0.58] },
        {
          t: 1.55,
          tip: [0, 0.88, -0.76],
          grip: [0, 1.06, -0.58],
          label: "zanshin",
          kamae: "gedan",
        },
        { t: 2.45, tip: [0, 0.88, -0.76], grip: [0, 1.06, -0.58] },
        { t: 3.6, tip: [0, 2.02, -0.76], grip: [0, 1.72, -0.58] },
      ],
    },
    seijiLines: [
      "The cut does not end where it lands. Finish the arc. Stay awake after it.",
      "...Someone I knew stopped his cuts early, too.",
    ],
    training: {
      reps: 3,
      feedback: "yes-no",
      replayable: "infinite",
      tolerance: "generous",
      drill: "Cut and hold the finish instead of stopping at the target.",
      check: {
        label: "follow-through",
        dir: "down",
        laneX: 0,
        height: 1.48,
        prompt: "Cut through the target. Keep the blade alive past impact.",
        success: "The cut continued. That is zanshin beginning.",
        correction:
          "Do not stop at the target. Finish the arc and stay awake after it.",
      },
    },
    beginnerError: "stopping dead at impact",
    correction:
      "You froze. A dead blade invites the next attack. Follow through.",
    skippable: true,
    unlocks: "phase III zanshin scoring pressure",
    arcBeat: "Seiji almost names someone from his past, then stops.",
  },
  {
    phase: 4,
    title: "Kesa",
    element: "descending diagonal (kesagiri)",
    kamaeTaught: "hasso-no-kamae",
    elicitByPlacement: "high-outside targets descend toward the opposite hip",
    ghostDemo: {
      title: "Hasso to kesa",
      description: "Shoulder to opposite hip along the sash line.",
      loopSeconds: 3.8,
      frames: [
        {
          t: 0,
          tip: [-0.38, 1.96, -0.76],
          grip: [-0.12, 1.68, -0.58],
          label: "hasso",
          kamae: "hasso",
        },
        {
          t: 1.25,
          tip: [0.42, 1.0, -0.76],
          grip: [0.18, 1.18, -0.58],
          label: "kesa",
        },
        { t: 2.35, tip: [0.42, 1.0, -0.76], grip: [0.18, 1.18, -0.58] },
        { t: 3.8, tip: [-0.38, 1.96, -0.76], grip: [-0.12, 1.68, -0.58] },
      ],
    },
    seijiLines: [
      "From the shoulder - hasso - the blade falls in the kesa line, shoulder to hip, named for the monk's sash.",
      "You are less angry today. I noticed.",
    ],
    training: {
      reps: 4,
      feedback: "yes-no",
      replayable: "infinite",
      tolerance: "generous",
      drill: "Cut the descending diagonal from high outside to low inside.",
      check: {
        label: "kesagiri",
        dir: "down-right",
        laneX: 0.24,
        height: 1.62,
        prompt: "From high outside, cut down to the opposite hip.",
        success: "Shoulder to hip. The kesa line is visible.",
        correction: "Diagonal. Follow the sash line, not a vertical drop.",
      },
    },
    beginnerError: "vertical or horizontal cut instead of a diagonal",
    correction: "Diagonal. Follow the sash line.",
    skippable: true,
    unlocks: "phase IV descending diagonal targets",
    arcBeat: "Seiji notices discipline beginning to replace rage.",
  },
  {
    phase: 5,
    title: "Kiriage",
    element: "rising diagonal",
    kamaeTaught: "gedan-no-kamae",
    elicitByPlacement: "low targets rise toward high lines",
    ghostDemo: {
      title: "Gedan to kiriage",
      description: "The same diagonal line, reversed upward.",
      loopSeconds: 3.7,
      frames: [
        {
          t: 0,
          tip: [-0.38, 0.92, -0.76],
          grip: [-0.16, 1.12, -0.58],
          label: "gedan",
          kamae: "gedan",
        },
        {
          t: 1.2,
          tip: [0.42, 1.9, -0.76],
          grip: [0.12, 1.64, -0.58],
          label: "kiriage",
        },
        { t: 2.3, tip: [0.42, 1.9, -0.76], grip: [0.12, 1.64, -0.58] },
        { t: 3.7, tip: [-0.38, 0.92, -0.76], grip: [-0.16, 1.12, -0.58] },
      ],
    },
    seijiLines: [
      "Now from below - gedan, the low guard - and rise. The same line, reversed.",
      "The man who beat you. Genza. He cut with angles you never saw coming. Now - you begin to see them.",
    ],
    training: {
      reps: 4,
      feedback: "yes-no",
      replayable: "infinite",
      tolerance: "generous",
      drill: "Cut upward from hip to shoulder.",
      check: {
        label: "kiriage",
        dir: "up-right",
        laneX: -0.18,
        height: 1.12,
        prompt: "Start low and rise from hip to shoulder.",
        success: "The same line reversed. Low guard becomes rising cut.",
        correction: "Up from hip to shoulder. Do not turn it into kesa.",
      },
    },
    beginnerError: "confusing upward and downward diagonals",
    correction: "Up, from hip to shoulder. The reverse of the kesa.",
    skippable: true,
    unlocks: "phase V rising diagonal targets",
    arcBeat: "Genza is named; the revenge thread becomes explicit.",
  },
  {
    phase: 6,
    title: "Renzoku",
    element: "chaining cuts and sustaining combo",
    ghostDemo: {
      title: "One cut invites the next",
      description: "Three cuts linked without a dead pause.",
      loopSeconds: 4.2,
      frames: [
        {
          t: 0,
          tip: [0, 2.02, -0.76],
          grip: [0, 1.72, -0.58],
          label: "one",
          kamae: "jodan",
        },
        { t: 0.8, tip: [0, 0.95, -0.76], grip: [0, 1.1, -0.58] },
        {
          t: 1.5,
          tip: [0.55, 1.42, -0.76],
          grip: [0.14, 1.34, -0.58],
          label: "two",
        },
        {
          t: 2.25,
          tip: [-0.4, 1.0, -0.76],
          grip: [-0.16, 1.16, -0.58],
          label: "three",
        },
        { t: 3.1, tip: [-0.4, 1.0, -0.76], grip: [-0.16, 1.16, -0.58] },
        { t: 4.2, tip: [0, 2.02, -0.76], grip: [0, 1.72, -0.58] },
      ],
    },
    seijiLines: [
      "One cut invites the next. Chain them - do not break the rhythm.",
      "This is what you lacked. Never strength. Flow.",
    ],
    training: {
      reps: 3,
      feedback: "yes-no",
      replayable: "infinite",
      tolerance: "generous",
      drill: "Land clean cuts without stopping to reset your hands.",
      checks: [
        {
          label: "chain 1",
          dir: "down",
          laneX: 0,
          height: 1.64,
          prompt: "First cut: shomen.",
          sequenceGroup: "renzoku",
          success: "One.",
        },
        {
          label: "chain 2",
          dir: "right",
          laneX: -0.24,
          height: 1.42,
          prompt: "Let the finish become a yoko cut.",
          sequenceGroup: "renzoku",
          success: "Two. No dead pause.",
        },
        {
          label: "chain 3",
          dir: "down-left",
          laneX: 0.24,
          height: 1.52,
          prompt: "Finish with a diagonal. No reset.",
          sequenceGroup: "renzoku",
          success: "Three. The chain held.",
          correction: "You stopped to reset. Let one cut invite the next.",
        },
      ],
    },
    beginnerError: "breaking cadence between cuts",
    correction: "You stopped to think. Don't think. Flow.",
    skippable: true,
    unlocks: "phase VI combo chain pressure",
    arcBeat: "Masaru's flaw is diagnosed: force without flow.",
  },
  {
    phase: 7,
    title: "Kata",
    element: "first long kata sequence",
    ghostDemo: {
      title: "A sentence with a blade",
      description: "Watch the sequence, then mirror its order.",
      loopSeconds: 5.0,
      frames: [
        {
          t: 0,
          tip: [0, 2.08, -0.76],
          grip: [0, 1.76, -0.58],
          label: "begin",
          kamae: "jodan",
        },
        { t: 0.7, tip: [0, 0.95, -0.76], grip: [0, 1.1, -0.58] },
        { t: 1.4, tip: [0.55, 1.42, -0.76], grip: [0.14, 1.34, -0.58] },
        { t: 2.1, tip: [-0.4, 1.88, -0.76], grip: [-0.12, 1.64, -0.58] },
        { t: 2.8, tip: [0.42, 1.0, -0.76], grip: [0.18, 1.18, -0.58] },
        {
          t: 3.55,
          tip: [0.42, 1.0, -0.76],
          grip: [0.18, 1.18, -0.58],
          label: "zanshin",
        },
        { t: 5.0, tip: [0, 2.08, -0.76], grip: [0, 1.76, -0.58] },
      ],
    },
    seijiLines: [
      "A kata is a sentence written with a blade. Memorize it - then forget it, and simply do it.",
      "I learned this form from someone I failed. Cut.",
    ],
    training: {
      reps: 3,
      feedback: "yes-no",
      replayable: "infinite",
      tolerance: "generous",
      drill: "Mirror the sequence order; the order is the meaning.",
      checks: [
        {
          label: "kata 1",
          dir: "down",
          laneX: 0,
          height: 1.64,
          prompt: "Sentence begins: shomen.",
          sequenceGroup: "kata",
          success: "First word.",
        },
        {
          label: "kata 2",
          dir: "right",
          laneX: -0.28,
          height: 1.42,
          prompt: "Second word: yoko.",
          sequenceGroup: "kata",
          success: "Second word.",
        },
        {
          label: "kata 3",
          dir: "down-right",
          laneX: -0.28,
          height: 1.6,
          prompt: "Third word: kesa.",
          sequenceGroup: "kata",
          success: "The sentence is forming.",
        },
        {
          label: "kata zanshin",
          dir: "any",
          laneX: 0.28,
          height: 1.18,
          prompt: "Finish the form and hold awareness.",
          sequenceGroup: "kata",
          success: "The order held. That is kata.",
          correction: "Not yet. Watch once more. The order is the meaning.",
        },
      ],
    },
    beginnerError: "wrong sequence order",
    correction: "Not yet. Watch once more. The order is the meaning.",
    skippable: true,
    unlocks: "phase VII kata sequence",
    arcBeat:
      "Seiji admits there was someone he failed; the memorial is implied.",
  },
  {
    phase: 8,
    title: "Happo",
    element: "all eight directions",
    kamaeTaught: "flowing between guards",
    elicitByPlacement:
      "targets at all heights and angles connect guard transitions",
    ghostDemo: {
      title: "Eight directions",
      description: "The complete vocabulary in one flowing cycle.",
      loopSeconds: 5.4,
      frames: [
        {
          t: 0,
          tip: [0, 2.1, -0.76],
          grip: [0, 1.78, -0.58],
          label: "jodan",
          kamae: "flowing",
        },
        { t: 0.55, tip: [0, 0.94, -0.76], grip: [0, 1.1, -0.58] },
        { t: 1.1, tip: [0.55, 1.42, -0.76], grip: [0.14, 1.34, -0.58] },
        { t: 1.65, tip: [-0.55, 1.42, -0.76], grip: [-0.14, 1.34, -0.58] },
        { t: 2.2, tip: [0.42, 1.9, -0.76], grip: [0.12, 1.64, -0.58] },
        { t: 2.75, tip: [-0.42, 1.9, -0.76], grip: [-0.12, 1.64, -0.58] },
        { t: 3.3, tip: [0.42, 1.0, -0.76], grip: [0.18, 1.18, -0.58] },
        { t: 3.85, tip: [-0.42, 1.0, -0.76], grip: [-0.18, 1.18, -0.58] },
        { t: 4.5, tip: [0, 0.94, -0.76], grip: [0, 1.1, -0.58] },
        { t: 5.4, tip: [0, 2.1, -0.76], grip: [0, 1.78, -0.58] },
      ],
    },
    seijiLines: [
      "All eight directions. No opening left unanswered.",
      "You are not the man who arrived at my door.",
    ],
    training: {
      reps: 4,
      feedback: "yes-no",
      replayable: "infinite",
      tolerance: "generous",
      drill:
        "Let one cut become the next across the full eight-way vocabulary.",
      checks: [
        {
          label: "1 shomen",
          dir: "down",
          laneX: 0,
          height: 1.72,
          prompt: "1/8 - straight down.",
          sequenceGroup: "happo",
          success: "One direction answered.",
        },
        {
          label: "2 yoko right",
          dir: "right",
          laneX: -0.3,
          height: 1.42,
          prompt: "2/8 - horizontal right.",
          sequenceGroup: "happo",
        },
        {
          label: "3 yoko left",
          dir: "left",
          laneX: 0.3,
          height: 1.42,
          prompt: "3/8 - horizontal left.",
          sequenceGroup: "happo",
        },
        {
          label: "4 kesa right",
          dir: "down-right",
          laneX: -0.22,
          height: 1.62,
          prompt: "4/8 - descending diagonal.",
          sequenceGroup: "happo",
        },
        {
          label: "5 kesa left",
          dir: "down-left",
          laneX: 0.22,
          height: 1.62,
          prompt: "5/8 - descending diagonal.",
          sequenceGroup: "happo",
        },
        {
          label: "6 kiriage right",
          dir: "up-right",
          laneX: -0.22,
          height: 1.14,
          prompt: "6/8 - rising diagonal.",
          sequenceGroup: "happo",
        },
        {
          label: "7 kiriage left",
          dir: "up-left",
          laneX: 0.22,
          height: 1.14,
          prompt: "7/8 - rising diagonal.",
          sequenceGroup: "happo",
        },
        {
          label: "8 finish",
          dir: "down",
          laneX: 0,
          height: 1.62,
          prompt: "8/8 - return to the center and finish.",
          sequenceGroup: "happo",
          success: "All lines answered. No gaps.",
          correction: "Let one cut become the next. No gaps.",
        },
      ],
    },
    beginnerError: "fumbled transition between guards",
    correction: "Let one cut become the next. No gaps.",
    skippable: true,
    unlocks: "phase VIII full happo-giri challenge",
    arcBeat: "Seiji acknowledges Masaru's transformation.",
  },
  {
    phase: 9,
    title: "Zanshin",
    element: "precision under pressure",
    ghostDemo: {
      title: "Fast, but clean",
      description: "Speed rises without losing the finish.",
      loopSeconds: 4.2,
      frames: [
        {
          t: 0,
          tip: [0, 2.02, -0.76],
          grip: [0, 1.72, -0.58],
          label: "clean",
          kamae: "jodan",
        },
        { t: 0.55, tip: [0, 0.95, -0.76], grip: [0, 1.1, -0.58] },
        { t: 1.05, tip: [0.55, 1.42, -0.76], grip: [0.14, 1.34, -0.58] },
        { t: 1.55, tip: [-0.38, 1.0, -0.76], grip: [-0.16, 1.18, -0.58] },
        {
          t: 2.3,
          tip: [-0.38, 1.0, -0.76],
          grip: [-0.16, 1.18, -0.58],
          label: "hold",
        },
        { t: 4.2, tip: [0, 2.02, -0.76], grip: [0, 1.72, -0.58] },
      ],
    },
    seijiLines: [
      "Fast, now. But clean. Speed without form is only noise.",
      "Almost. One more climb.",
    ],
    training: {
      reps: 5,
      feedback: "yes-no",
      replayable: "infinite",
      tolerance: "generous",
      drill: "Cut quickly, then still finish the form.",
      checks: [
        {
          label: "clean speed 1",
          dir: "down",
          laneX: 0,
          height: 1.62,
          requireTenouchi: true,
          prompt: "Fast, but still a full shomen.",
          sequenceGroup: "pressure",
          success: "Speed without noise.",
        },
        {
          label: "clean speed 2",
          dir: "right",
          laneX: -0.24,
          height: 1.42,
          requireTenouchi: true,
          prompt: "Keep the line clean under pressure.",
          sequenceGroup: "pressure",
          success: "The line stayed true.",
        },
        {
          label: "zanshin hold",
          dir: "down-left",
          laneX: 0.24,
          height: 1.58,
          prompt: "Finish the diagonal and do not collapse after it.",
          sequenceGroup: "pressure",
          success: "Fast and finished. That is zanshin under pressure.",
          correction: "You traded truth for speed. Keep both.",
        },
      ],
    },
    beginnerError: "haste breaking form",
    correction: "You traded truth for speed. Keep both.",
    skippable: true,
    unlocks: "phase IX fast clean targets",
    arcBeat: "Final pressure test before graduation.",
  },
  {
    phase: 10,
    title: "Menkyo",
    element: "graduation",
    ghostDemo: {
      title: "No new form",
      description: "The player performs; Seiji watches.",
      loopSeconds: 4.0,
      frames: [
        {
          t: 0,
          tip: [0, 1.58, -0.76],
          grip: [0, 1.28, -0.58],
          label: "chudan",
          kamae: "chudan",
        },
        { t: 1.0, tip: [0, 2.08, -0.76], grip: [0, 1.76, -0.58] },
        { t: 1.8, tip: [0, 0.96, -0.76], grip: [0, 1.1, -0.58] },
        {
          t: 2.8,
          tip: [0, 0.96, -0.76],
          grip: [0, 1.1, -0.58],
          label: "zanshin",
        },
        { t: 4.0, tip: [0, 1.58, -0.76], grip: [0, 1.28, -0.58] },
      ],
    },
    seijiLines: [
      "You came here to learn how to win. You learned how to be worth the winning.",
      "This dojo has nothing left to teach you.",
      "But the world beyond these walls has not forgotten you. Genza still breathes.",
      "There are things - about me, about the blade I gave you - that I have not told you.",
      "Your training is over, Masaru. Your journey begins.",
    ],
    training: {
      reps: 1,
      feedback: "yes-no",
      replayable: "infinite",
      tolerance: "generous",
      drill: "Perform with everything combined.",
      checks: [
        {
          label: "kamae",
          dir: "down",
          laneX: 0,
          height: 1.68,
          requireTenouchi: true,
          prompt: "Begin from guard. One honest shomen.",
          sequenceGroup: "menkyo",
          success: "The beginning is clean.",
        },
        {
          label: "flow",
          dir: "right",
          laneX: -0.26,
          height: 1.42,
          prompt: "Flow into yoko without losing the grip.",
          sequenceGroup: "menkyo",
          success: "Flow held.",
        },
        {
          label: "angle",
          dir: "up-right",
          laneX: -0.2,
          height: 1.12,
          prompt: "Rise from gedan. Complete the line.",
          sequenceGroup: "menkyo",
          success: "The angle is no longer strange.",
        },
        {
          label: "zanshin",
          dir: "down-left",
          laneX: 0.24,
          height: 1.6,
          prompt: "Last cut. Finish awake.",
          sequenceGroup: "menkyo",
          success: "Everything together. The dojo has taught what it can.",
          correction: "Everything together. Guard, grip, awareness. Again.",
        },
      ],
    },
    beginnerError: "forgetting the whole while chasing one technique",
    correction: "Everything together. Guard, grip, awareness. Again.",
    skippable: true,
    unlocks: "graduation end card and v2 hook",
    arcBeat:
      "The preview resolves Masaru's internal arc while leaving Genza, Seiji's secret, and the world beyond open.",
  },
];

export const ALL_TUTORIALS: Tutorial[] = [
  ONBOARDING_TUTORIAL,
  ...PHASE_TUTORIALS,
];

export function getTutorialByPhase(phase: number): Tutorial | undefined {
  if (phase === 0) return ONBOARDING_TUTORIAL;
  return PHASE_TUTORIALS.find((tutorial) => tutorial.phase === phase);
}

export function getTutorialForCampaignPhase(
  campaignPhaseIndex: number,
): Tutorial | undefined {
  return getTutorialByPhase(campaignPhaseIndex + 1);
}

export function getTutorialStorageKey(phase: number): string {
  return phase === 0 ? "onboarding" : `phase-${phase}`;
}
